import { Link, useParams } from "react-router-dom";
import { STATES_SEO, APPROVALS, approvalByKey, truncate } from "../../seo/explore-data";
import { PageSeo, Crumbs, CtaBlock, Disclaimer, Faq, SectionH, KvTable } from "./ExploreBits";
import NotFound from "../NotFound";

const NoteCard = ({ title, children }) => (
  <div className="bg-white border border-[#0B1F3A]/10 rounded-xl px-5 py-4">
    <h3 className="font-serif font-semibold text-[#0B1F3A] text-sm">{title}</h3>
    <p className="text-sm text-[#475569] mt-1">{children}</p>
  </div>
);

export default function StateDetail() {
  const { slug } = useParams();
  const s = STATES_SEO.find((x) => x.slug === slug);
  if (!s) return <NotFound />;

  const stateApprovals = APPROVALS.filter((a) => (a.variants || []).some((v) => (v.states || []).includes(s.name)));
  const nationalTop = ["gna_ctu", "sec68", "ceig", "land_title", "row_framework", "sldc_charging"].map((k) => approvalByKey(k)).filter(Boolean);
  const landLinks = [approvalByKey("na_conversion"), approvalByKey("land_title")].filter(Boolean);

  const faqs = [
    { q: `Who is the State Transmission Utility (STU) in ${s.name}?`, a: `${s.stuFull}. Intra-state (InSTS) connectivity applications for utility-scale projects are made to ${s.stu}.` },
    { q: `Which agency handles renewable energy approvals in ${s.name}?`, a: `${s.nodalFull} is the state nodal agency; the state regulator is ${s.ercFull}.` },
    { q: `What are the open access rules in ${s.name}?`, a: `${s.oa}. Verify the currently applicable regulation with ${s.erc}.` },
    { q: `What are the banking rules for green energy in ${s.name}?`, a: `${s.banking}. Banking rules change frequently — verify with the discom/${s.erc}.` },
    { q: `What is the net-metering cap in ${s.name}?`, a: `Rooftop net metering is capped around ${s.nmCapKw} kW per the state regulation — verify the current cap and any network charges with the discom.` },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="state-detail-page">
      <PageSeo title={`${s.name} Solar & Wind Project Approvals | RegNav`}
        description={truncate(`Complete regulatory roadmap for solar, wind, BESS and transmission projects in ${s.name} — ${s.stu}, ${s.nodal}, ${s.erc}, CEIG, open access, banking and net-metering rules.`, 158)}
        path={`/states/${s.slug}/`} />
      <Crumbs items={[{ name: "Home", to: "/" }, { name: "States", to: "/states" }, { name: s.name }]} />
      <h1 className="font-serif text-3xl sm:text-4xl text-[#0B1F3A]">Solar &amp; wind project approvals in {s.name}</h1>
      <p className="text-sm text-[#475569] mt-3 max-w-2xl">Everything a developer, EPC or industrial consumer needs to navigate {s.name}'s power-project approvals: the state agencies, open-access and banking rules, net-metering caps, land rules and the state-specific steps layered on top of national approvals.</p>

      <SectionH>Key agencies in {s.name}</SectionH>
      <KvTable rows={[
        ["State Transmission Utility (STU)", s.stuFull],
        ["Distribution companies (discoms)", s.discoms],
        ["State nodal agency (RE)", s.nodalFull],
        ["Electricity regulator", s.ercFull],
        ["Electrical safety (CEIG)", `${s.ceig} — verify the jurisdictional office`],
      ]} />

      <SectionH>Open access, banking &amp; net metering in {s.name}</SectionH>
      <div className="grid sm:grid-cols-2 gap-4">
        <NoteCard title="Green energy open access">{s.oa}.</NoteCard>
        <NoteCard title="Banking">{s.banking}.</NoteCard>
        <NoteCard title="Net metering">Rooftop net metering capped around {s.nmCapKw} kW per current state regulation — verify with the discom.</NoteCard>
        <NoteCard title="Worth knowing">{s.extra}.</NoteCard>
      </div>

      <SectionH>Land rules</SectionH>
      <p className="text-sm text-[#475569] max-w-2xl">{s.land}.{landLinks.length > 0 && <> See the national guides for {landLinks.map((a, i) => <span key={a.key}>{i > 0 && " and "}<Link to={`/approvals/${a.slug}`} className="text-[#1D4ED8] hover:underline">{a.name.toLowerCase()}</Link></span>)}.</>}</p>

      {stateApprovals.length > 0 && (
        <>
          <SectionH>Approvals with {s.name}-specific procedures</SectionH>
          <ul className="list-disc pl-6 text-sm space-y-1.5">
            {stateApprovals.map((a) => <li key={a.key}><Link to={`/approvals/${a.slug}`} className="text-[#1D4ED8] hover:underline">{a.name}</Link> — {truncate(a.plain_summary, 110)}</li>)}
          </ul>
        </>
      )}

      <SectionH>National approvals that apply in {s.name}</SectionH>
      <ul className="list-disc pl-6 text-sm space-y-1">
        {nationalTop.map((a) => <li key={a.key}><Link to={`/approvals/${a.slug}`} className="text-[#1D4ED8] hover:underline">{a.name}</Link></li>)}
      </ul>
      <p className="text-sm text-[#475569] mt-4">Browse <Link to="/approvals" className="text-[#1D4ED8] hover:underline">all {APPROVALS.length} approvals</Link>, or generate the exact list for your project:</p>
      <CtaBlock />
      <Faq faqs={faqs} />
      <Disclaimer>State rules change frequently. This page reflects the RegNav knowledge base — indicative guidance, not legal advice. Verify with {s.erc}, {s.stu} and the concerned departments.</Disclaimer>
    </div>
  );
}
